import React, { useState } from "react";
import { toast } from "sonner";
import { Flag, Loader2, X } from "lucide-react";
import { sharedApi } from "@/lib/api";

const REASONS = [
  { key: "prohibited", label: "Prohibited item (weapons, drugs, etc.)" },
  { key: "scam_or_fraud", label: "Scam or fraud" },
  { key: "wrong_category", label: "Wrong category" },
  { key: "offensive", label: "Offensive content" },
  { key: "counterfeit", label: "Counterfeit or fake" },
  { key: "duplicate", label: "Duplicate listing" },
  { key: "other", label: "Other" },
];

/**
 * Report Listing modal (iter-227 mirror form). Anonymous submits allowed
 * (backend rate-limits to 5/min/IP). Reason is a required radio; detail
 * is optional free text ≤500 chars. Success closes the modal and shows
 * a confirmation toast; further reports on the same listing are still
 * allowed by the backend (moderators dedupe upstream).
 */
export default function ReportListingDialog({ listingId, open, onClose }) {
  const [reason, setReason] = useState("prohibited");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sharedApi.post(`/classifieds/listings/${listingId}/report`, {
        reason,
        detail: detail.trim().slice(0, 500) || undefined,
      });
      toast.success("Thanks — your report is with our moderators.");
      setDetail("");
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("You're reporting too quickly — try again in a minute.");
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't send report");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="ad-report-dialog"
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <header className="px-6 py-4 border-b border-subtle flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900">
            <Flag className="w-4 h-4 text-red-600" /> Report this listing
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-6 py-4 space-y-4">
          <fieldset className="space-y-1.5" data-testid="ad-report-reason">
            <legend className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
              Why are you reporting this?
            </legend>
            {REASONS.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="radio"
                  name="report-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                  className="text-[color:var(--brand-blue)] focus:ring-[color:var(--brand-blue)]"
                />
                {r.label}
              </label>
            ))}
          </fieldset>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1.5 block">
              Details (optional)
            </span>
            <textarea
              data-testid="ad-report-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, 500))}
              className="w-full min-h-24 rounded-xl border border-soft px-3 py-2 text-sm focus:border-[color:var(--brand-blue)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-blue)]/20"
              maxLength={500}
              placeholder="Anything a moderator should know…"
            />
            <span className="text-[11px] text-slate-400">{detail.length}/500</span>
          </label>
        </div>
        <footer className="px-6 py-4 border-t border-subtle flex justify-end gap-2 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="ad-report-submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 text-sm disabled:opacity-60 transition-colors"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send report
          </button>
        </footer>
      </form>
    </div>
  );
}
